import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import Modal from '../../components/Modal';

const GitTestCaseModal = ({
  onClose = () => { },
  onSubmit = () => { },
  loading = false,
  parent = {},
}) => {
  const [filePath, setFilePath] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [lastToken, setLastToken] = useState(null);

  // Get branches from Redux state
  const branches = useSelector((state) => state.hosting.branches || []);

  // Helper function to strip .git suffix from URL (only for branch URLs)
  const stripGitSuffix = (url) => {
    if (!url) return '';
    return url.trim().replace(/\.git$/, '');
  };

  // Get git repo URL - prioritize from branches, fallback to parent.gitrepourl
  const getGitRepoUrl = () => {
    // First, try to find git repo from branches that matches the project's team and AWS account
    if (branches.length > 0 && parent.teamname && parent.awsaccountid) {
      const matchingBranch = branches.find(
        (branch) => 
          branch.teamname === parent.teamname && 
          branch.awsaccountid === parent.awsaccountid
      );
      
      if (matchingBranch && matchingBranch.gitrepo) {
        // Strip .git suffix only from branch URLs (from Manage Git Branches)
        return stripGitSuffix(matchingBranch.gitrepo);
      }
    }

    // Fallback to parent.gitrepourl (already clean from project settings)
    return parent.gitrepourl ? parent.gitrepourl.trim() : '';
  };

  const gitRepoUrl = getGitRepoUrl();
  const gitRepoSource = branches.some(
    (branch) => 
      branch.teamname === parent.teamname && 
      branch.awsaccountid === parent.awsaccountid &&
      branch.gitrepo
  ) ? 'Manage Git Branches' : 'project settings';

  useEffect(() => {
    // Check if we have a recent token from sessionStorage
    const savedToken = sessionStorage.getItem('github_access_token');
    if (savedToken) {
      setIsConnected(true);
      setLastToken(savedToken);
    }
  }, []);

  const connectToGitHub = () => {
    const githubOAuthUrl = `${process.env.REACT_APP_GITHUB_ENTERPRISE_URL}/login/oauth/authorize?` +
      `client_id=${process.env.REACT_APP_GITHUB_CLIENT_ID}&` +
      `scope=repo,user&` +
      `redirect_uri=${encodeURIComponent(window.location.origin + '/auth/github/callback')}`;

    window.location.href = githubOAuthUrl;
  };

  const forceReconnect = () => {
    // Clear any existing token and force re-authorization
    sessionStorage.removeItem('github_access_token');
    setIsConnected(false);
    setLastToken(null);

    const githubOAuthUrl = `${process.env.REACT_APP_GITHUB_ENTERPRISE_URL}/login/oauth/authorize?` +
      `client_id=${process.env.REACT_APP_GITHUB_CLIENT_ID}&` +
      `scope=repo,user&` +
      `prompt=consent&` + // Force GitHub to show authorization screen
      `redirect_uri=${encodeURIComponent(window.location.origin + '/auth/github/callback')}`;

    window.location.href = githubOAuthUrl;
  };

  const handleSubmit = async () => {
    const payload = {
      gitRepoUrl: gitRepoUrl.trim(),
      filePath: filePath.trim(),
      projectname: parent.projectname,
      branchName: parent.gitbranchname,
      tools: parent.tools,
      githubToken: lastToken, // Include token if available
    };

    console.log('📤 Submitting payload:', payload);
    onSubmit(payload);
  };

  const isFormValid = filePath.trim() && gitRepoUrl.trim();

  return (
    <Modal
      title="Import Test Cases from Git Repository"
      onClose={onClose}
      onSubmit={handleSubmit}
      loading={loading}
      submitButtonText="Import Test Cases"
      submitEnabled={isFormValid && !loading}
    >
      <div className="modal-form modal-content-wrapper">
        {/* GitHub Connection Status */}
        {isConnected ? (
          <div className="alert alert-success">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <strong>✅ GitHub Connected</strong>
                <p className="mb-0">Ready to access repositories.</p>
              </div>
              <button className="btn btn-sm btn-outline-secondary" onClick={forceReconnect}>
                Reconnect
              </button>
            </div>
          </div>
        ) : (
          <div className="alert alert-info">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <strong>GitHub Authentication</strong>
                <p className="mb-0">Connect to GitHub to access repositories.</p>
              </div>
              <button className="btn btn-primary" onClick={connectToGitHub}>
                Connect GitHub
              </button>
            </div>
          </div>
        )}

        <div className="row">
          <div className="col-sm-12 form-group">
            <label htmlFor="projectGitRepo">Project Git Repository</label>
            <div className="form-control-plaintext bg-light p-2 rounded">
              {gitRepoUrl || 'No Git repository configured for this project'}
            </div>
            <small className="form-text text-muted">
              {gitRepoUrl 
                ? `Using the Git repository URL from ${gitRepoSource}` 
                : 'Please configure a Git repository URL in the project settings or Manage Git Branches'}
            </small>
          </div>
        </div>

        <div className="row">
          <div className="col-sm-12 form-group">
            <label htmlFor="filePath">File Path *</label>
            <input
              type="text"
              className="form-control"
              id="filePath"
              value={filePath}
              onChange={(e) => setFilePath(e.target.value)}
              placeholder="tests/**/*.spec.js or specific/test/file.js"
            />
            <small className="form-text text-muted">
              Specify the path to test files or use glob patterns (e.g., *.spec.js, **/*.test.js)
            </small>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default GitTestCaseModal; 